import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AI_HISTORY_KEY,
  AI_SCHEMA_CACHE_KEY,
  AI_SESSIONS_KEY,
  CONNECTION_AUDIT_QUERY_KEY,
  CONNECTION_CENTER_QUERY_KEY,
  CONNECTION_HISTORY_QUERY_KEY,
  CONNECTION_QUERY_KEY,
  AGENT_HISTORY_KEY,
  AGENTS_KEY,
  WORKFLOWS_KEY,
  WORKFLOW_RUNS_KEY,
} from "../constants";
import {
  createStudioConnection,
  duplicateStudioConnection,
  listStudioConnections,
  setConnectionActive,
  setConnectionArchived,
  setConnectionFavorite,
  setConnectionStatus,
  updateStudioConnection,
} from "../services/connectionService";
import { fetchConnectionAuditLogs, logConnectionAudit } from "../services/connectionAuditService";
import { verifyStudioConnection } from "../services/connectionVerifyService";
import {
  enterpriseDeleteConnection,
  enterpriseDeleteConnectionsBulk,
} from "../services/connectionDeleteService";

function actorFromUser(user) {
  return {
    adminUid: user?.uid || "",
    adminName: user?.email || user?.username || "",
  };
}

function connectionsKey(projectId) {
  return projectId ? [...CONNECTION_QUERY_KEY, projectId] : CONNECTION_QUERY_KEY;
}

export function useAllStudioConnections() {
  return useQuery({
    queryKey: CONNECTION_QUERY_KEY,
    queryFn: () => listStudioConnections(),
    staleTime: 30_000,
  });
}

export function useProjectConnections(projectId) {
  return useQuery({
    queryKey: connectionsKey(projectId),
    queryFn: () => listStudioConnections({ projectId }),
    enabled: Boolean(projectId),
    staleTime: 30_000,
  });
}

export function useConnectionAuditLogs(filters = {}) {
  return useQuery({
    queryKey: [...CONNECTION_AUDIT_QUERY_KEY, filters],
    queryFn: () => fetchConnectionAuditLogs({ max: 50, ...filters }),
    staleTime: 15_000,
  });
}

export function useConnectionMutations(user, projectIdFilter) {
  const queryClient = useQueryClient();
  const actor = actorFromUser(user);

  const invalidateAll = async () => {
    await queryClient.invalidateQueries({ queryKey: CONNECTION_QUERY_KEY });
    if (projectIdFilter) {
      await queryClient.invalidateQueries({ queryKey: connectionsKey(projectIdFilter) });
    }
    await queryClient.invalidateQueries({ queryKey: CONNECTION_AUDIT_QUERY_KEY });
    await queryClient.invalidateQueries({ queryKey: CONNECTION_HISTORY_QUERY_KEY });
    await queryClient.invalidateQueries({ queryKey: CONNECTION_CENTER_QUERY_KEY });
    await queryClient.invalidateQueries({ queryKey: AI_SESSIONS_KEY });
    await queryClient.invalidateQueries({ queryKey: AI_HISTORY_KEY });
    await queryClient.invalidateQueries({ queryKey: AI_SCHEMA_CACHE_KEY });
    await queryClient.invalidateQueries({ queryKey: AGENTS_KEY });
    await queryClient.invalidateQueries({ queryKey: AGENT_HISTORY_KEY });
    await queryClient.invalidateQueries({ queryKey: WORKFLOWS_KEY });
    await queryClient.invalidateQueries({ queryKey: WORKFLOW_RUNS_KEY });
  };

  const optimisticAll = (updater) => {
    const apply = (key) => {
      queryClient.setQueryData(key, (old) => {
        if (!Array.isArray(old)) return old;
        return updater(old);
      });
    };
    apply(CONNECTION_QUERY_KEY);
    if (projectIdFilter) apply(connectionsKey(projectIdFilter));
  };

  const createMutation = useMutation({
    mutationFn: (payload) => createStudioConnection(payload, actor.adminName),
    onSuccess: async (conn) => {
      await logConnectionAudit({
        action: "create",
        connectionId: conn.id,
        connectionName: conn.name,
        projectId: conn.projectId,
        ...actor,
      });
      await invalidateAll();
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, payload, existing }) =>
      updateStudioConnection(id, payload, existing, actor.adminName),
    onMutate: async ({ id, payload }) => {
      await queryClient.cancelQueries({ queryKey: CONNECTION_QUERY_KEY });
      const prev = queryClient.getQueryData(CONNECTION_QUERY_KEY);
      optimisticAll((list) =>
        list.map((c) => (c.id === id ? { ...c, ...payload, updatedAt: new Date().toISOString() } : c))
      );
      return { prev };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(CONNECTION_QUERY_KEY, ctx.prev);
    },
    onSuccess: async (conn) => {
      await logConnectionAudit({
        action: "update",
        connectionId: conn.id,
        connectionName: conn.name,
        projectId: conn.projectId,
        ...actor,
      });
      await invalidateAll();
    },
  });

  const archiveMutation = useMutation({
    mutationFn: ({ id, archived }) => setConnectionArchived(id, archived, actor.adminName),
    onSuccess: async (conn, { archived }) => {
      await logConnectionAudit({
        action: archived ? "archive" : "restore",
        connectionId: conn.id,
        connectionName: conn.name,
        projectId: conn.projectId,
        ...actor,
      });
      await invalidateAll();
    },
  });

  const favoriteMutation = useMutation({
    mutationFn: ({ id, favorite }) => setConnectionFavorite(id, favorite, actor.adminName),
    onMutate: async ({ id, favorite }) => {
      optimisticAll((list) => list.map((c) => (c.id === id ? { ...c, favorite } : c)));
    },
    onSuccess: async (conn, { favorite }) => {
      await logConnectionAudit({
        action: favorite ? "favorite" : "unfavorite",
        connectionId: conn.id,
        connectionName: conn.name,
        projectId: conn.projectId,
        ...actor,
      });
      await invalidateAll();
    },
  });

  const activateMutation = useMutation({
    mutationFn: (conn) => setConnectionActive(conn.id, true, actor.adminName),
    onSuccess: async (conn) => {
      await logConnectionAudit({
        action: "activated",
        connectionId: conn.id,
        connectionName: conn.name,
        projectId: conn.projectId,
        ...actor,
      });
      await invalidateAll();
    },
  });

  const deactivateMutation = useMutation({
    mutationFn: (conn) => setConnectionActive(conn.id, false, actor.adminName),
    onSuccess: async (conn) => {
      await logConnectionAudit({
        action: "deactivated",
        connectionId: conn.id,
        connectionName: conn.name,
        projectId: conn.projectId,
        ...actor,
      });
      await invalidateAll();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (params) => enterpriseDeleteConnection(params),
    onMutate: async ({ connection }) => {
      await queryClient.cancelQueries({ queryKey: CONNECTION_QUERY_KEY });
      const prev = queryClient.getQueryData(CONNECTION_QUERY_KEY);
      optimisticAll((list) => list.filter((c) => c.id !== connection.id));
      return { prev };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(CONNECTION_QUERY_KEY, ctx.prev);
    },
    onSuccess: async (result, params) => {
      await logConnectionAudit({
        action: "delete",
        connectionId: params.connection.id,
        connectionName: params.connection.name,
        projectId: params.connection.projectId,
        ...actor,
        details: { reason: params.reason || "" },
      });
      await invalidateAll();
      return result;
    },
  });

  const bulkDeleteMutation = useMutation({
    mutationFn: (params) => enterpriseDeleteConnectionsBulk(params),
    onMutate: async ({ connections }) => {
      const ids = new Set(connections.map((c) => c.id));
      await queryClient.cancelQueries({ queryKey: CONNECTION_QUERY_KEY });
      const prev = queryClient.getQueryData(CONNECTION_QUERY_KEY);
      optimisticAll((list) => list.filter((c) => !ids.has(c.id)));
      return { prev };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(CONNECTION_QUERY_KEY, ctx.prev);
    },
    onSuccess: async (_result, params) => {
      await Promise.all(
        params.connections.map((conn) =>
          logConnectionAudit({
            action: "delete",
            connectionId: conn.id,
            connectionName: conn.name,
            projectId: conn.projectId,
            ...actor,
            details: { reason: params.reason || "", bulk: true },
          })
        )
      );
      await invalidateAll();
    },
  });

  const duplicateMutation = useMutation({
    mutationFn: (sourceId) => duplicateStudioConnection(sourceId, actor.adminName),
    onSuccess: async (conn, sourceId) => {
      await logConnectionAudit({
        action: "duplicate",
        connectionId: conn.id,
        connectionName: conn.name,
        projectId: conn.projectId,
        ...actor,
        details: { sourceId },
      });
      await invalidateAll();
    },
  });

  const verifyMutation = useMutation({
    mutationFn: async (conn) => {
      const { result, status } = await verifyStudioConnection(conn.id, {
        verifiedBy: actor.adminName || actor.adminUid,
      });
      const updated = await setConnectionStatus(conn.id, status, {
        lastVerified: result.checkedAt,
      });
      await logConnectionAudit({
        action: "verified",
        connectionId: conn.id,
        connectionName: conn.name,
        projectId: conn.projectId,
        ...actor,
        details: {
          overall: result.overall,
          healthScore: result.healthScore,
          responseTimeMs: result.responseTimeMs,
          engine: result.engine,
        },
      });
      return { result, connection: updated };
    },
    onSuccess: async () => {
      await invalidateAll();
    },
  });

  return {
    createMutation,
    updateMutation,
    archiveMutation,
    favoriteMutation,
    activateMutation,
    deactivateMutation,
    deleteMutation,
    bulkDeleteMutation,
    duplicateMutation,
    verifyMutation,
  };
}
